/**
 * One column's filter: which operator is in force, and what it is filtering by.
 *
 * Pure, and deliberately React-free — `src/AGENTS.md` keeps components out of
 * `src/lib/`. The rendering half is `src/design-system/column-filter.tsx`; the
 * URL half is `src/hooks/use-table-query.ts`, which this module never touches.
 * Everything decided here is decided from a field description and a bag of
 * query values, which is what lets each rule be tested as an input and an
 * output rather than through a rendered screen.
 *
 * **A field owns several parameters and may use only one.** `pkid` accepts
 * seven — `pkid__exact`, `pkid__gt`, `pkid__range` and so on — and a reader
 * filtering by one of them must not leave a stale value in another, because the
 * endpoint would apply both. Every writer here therefore clears the field's
 * whole parameter set before it writes, and that is the single rule this module
 * exists to hold in one place.
 */

import type { TableQueryValueType } from './table-query.ts'

/** One `field__operator` pair the endpoint accepts. */
export interface ColumnFilterOperator {
  /** The operator alone, for example `icontains`. */
  op: string
  /** The query name the endpoint reads, for example `name__icontains`. */
  param: string
  /** How the operator reads in the choice list, for example `contains`. */
  label: string
  /** The spec's own example, shown as the control's placeholder. */
  example: string
}

/**
 * What a column can be filtered by.
 *
 * Structurally what the generated contracts already produce, so a generated
 * field is passed straight in rather than mapped. Nothing here is hand-written
 * per screen: a new operator appears when the contract is regenerated.
 */
/**
 * Whether the picker has a control for an operator.
 *
 * `isnull` asks for no value and every box in the picker asks for one, so a field may declare it —
 * the endpoint accepts it — and the picker still cannot offer it. The rule lives here rather than
 * in one screen because it is a fact about the CONTROL, not about any one report: a table whose
 * endpoint later declares `isnull` needs the same answer.
 *
 * @param operator - One operator from a generated contract.
 * @returns Whether a reader can enter a value for it.
 */
export function isRenderableOperator(operator: { op: string }): boolean {
  return operator.op !== 'isnull'
}

/** What every filterable column carries, whatever its control. */
interface ColumnFilterFieldBase {
  name: string
  /** kebab-case, so a `data-testid` built from it holds the locator policy. */
  slug: string
  label: string
  operators: readonly ColumnFilterOperator[]
}

/**
 * The control a value is entered with, for a column whose values are open.
 *
 * `date` gets a date picker and sends `YYYY-MM-DD`, which is what a calendar
 * column reads. It is separate from `text` because a reader should not have to
 * know the wire format, and separate from `number` because a date box that
 * accepted `20260901` would send a number the endpoint rejects.
 */
export type OpenFieldKind = 'text' | 'number' | 'boolean' | 'date'

/** A column a reader types or picks a single value into. */
export interface OpenColumnFilterField extends ColumnFilterFieldBase {
  kind: OpenFieldKind
  /** Never. Only a closed set has values, and the union is what says so. */
  values?: never
}

/**
 * A column whose values are a closed set the endpoint declares.
 *
 * It gets a picker over `values`, never a box. The endpoint refuses anything outside the set, so
 * a box can only produce two outcomes a reader did not want: a 400, or — when they guess a
 * plausible spelling — an empty report.
 */
export interface EnumColumnFilterField extends ColumnFilterFieldBase {
  kind: 'enum'
  /**
   * Every value the field accepts, in the endpoint's own order.
   *
   * Required, not optional. An enum whose values went missing would render an empty picker and
   * say nothing, which is the same silence this whole contract exists to remove — so the type
   * refuses to describe one rather than leaving it to a runtime check.
   *
   * The endpoint declares it, so a value added server-side reaches the console by regenerating
   * the contract rather than by editing a list here.
   */
  values: readonly string[]
}

/**
 * One column's filter declaration.
 *
 * A union on `kind`, so `values` is present exactly when it means something. Written as one
 * interface with an optional `values`, nothing stopped an `enum` field being built without them.
 */
export type ColumnFilterField = OpenColumnFilterField | EnumColumnFilterField

/** Query values as the controls read them: parameter name to text. */
export type FilterValues = Readonly<Record<string, string>>

/**
 * What separates the two halves of a range value on the wire.
 *
 * The endpoint reads `10..20`, which is a wire format rather than something a
 * reader should type — so the control offers two boxes and this joins them.
 */
export const RANGE_SEPARATOR = '..'

/** The operator whose value carries two halves. */
const RANGE_OP = 'range'

/** The operator whose value is a list rather than one thing. */
const LIST_OP = 'in'

/**
 * Whether an operator takes a pair of values rather than one.
 *
 * @param operator - The operator to test.
 * @returns True when its value is a range.
 */
export function isRange(operator: ColumnFilterOperator): boolean {
  return operator.op === RANGE_OP
}

/**
 * Whether an operator takes a list of values rather than one.
 *
 * `is any of` reads `1,2,3`. That matters to the control: a number box refuses a
 * comma, so an operator that takes a list has to be typed into a text box even
 * on a numeric column — otherwise the reader cannot enter the second value and
 * a stored `pkid__in` opens blank.
 *
 * @param operator - The operator to test.
 * @returns True when its value is a comma-separated list.
 */
export function isList(operator: ColumnFilterOperator): boolean {
  return operator.op === LIST_OP
}

/** What separates the values of a list on the wire. The endpoint reads `1,2,3`. */
export const LIST_SEPARATOR = ','

/**
 * A list value as the values a reader picked.
 *
 * Tolerant on the way in, like {@link splitRange}: a hand-written URL may carry spaces, a
 * trailing separator, or a value twice. Empty entries are dropped rather than sent, because an
 * empty value in a list is one the endpoint refuses.
 *
 * @param value - The stored list value.
 * @returns The values, in the order they were written.
 */
export function splitList(value: string): string[] {
  return value
    .split(LIST_SEPARATOR)
    .map(entry => entry.trim())
    .filter(entry => entry !== '')
}

/**
 * Values as the one the endpoint reads, or empty when nothing is picked.
 *
 * Empty rather than a bare separator: `withFilter` reads an empty value as the field being
 * cleared, which is what picking nothing means.
 *
 * @param values - The values picked.
 * @returns The joined value.
 */
export function joinList(values: readonly string[]): string {
  return values
    .map(entry => entry.trim())
    .filter(entry => entry !== '')
    .join(LIST_SEPARATOR)
}

/**
 * A list value with one value added or removed.
 *
 * Rebuilt in the FIELD's own order, never the order the reader picked. Otherwise the same
 * selection reached two different URLs depending on which value was ticked first, and a link
 * shared from one would not match the view it was copied from.
 *
 * @param value - The stored list value.
 * @param option - The value to toggle.
 * @param order - Every value the field accepts, in the endpoint's order.
 * @returns The new list value, empty when nothing is left picked.
 */
export function toggleListValue(
  value: string,
  option: string,
  order: readonly string[]
): string {
  const picked = new Set(splitList(value))

  if (!picked.delete(option)) {
    picked.add(option)
  }

  return joinList(order.filter(entry => picked.has(entry)))
}

/**
 * A range value as the two halves a reader edits.
 *
 * Tolerant on the way in: a value typed by hand, or left behind by an older
 * client, may have one half or neither. Anything past the second half is
 * dropped rather than folded into it, so `1..2..3` reads as `1` and `2` instead
 * of producing a value the endpoint would reject.
 *
 * @param value - The stored range value.
 * @returns The two halves, either of which may be empty.
 */
export function splitRange(value: string): { from: string; to: string } {
  const [from = '', to = ''] = value.split(RANGE_SEPARATOR)

  return { from: from.trim(), to: to.trim() }
}

/**
 * Two halves as the value the endpoint reads, or empty when it has no pair.
 *
 * A half-open range is NOT a filter this endpoint accepts — `__range` wants
 * both ends — so one half alone answers empty rather than something that would
 * 400. The control shows what the reader typed either way; this decides only
 * what is worth sending.
 *
 * @param from - The lower half.
 * @param to - The upper half.
 * @returns The joined value, or an empty string when either half is missing.
 */
export function joinRange(from: string, to: string): string {
  const lower = from.trim()
  const upper = to.trim()

  return lower === '' || upper === ''
    ? ''
    : `${lower}${RANGE_SEPARATOR}${upper}`
}

/**
 * Every parameter name this field can write.
 *
 * @param field - The field to read.
 * @returns Its parameter names.
 */
function paramsOf(field: ColumnFilterField): string[] {
  return field.operators.map(operator => operator.param)
}

/**
 * The operator currently holding a value, if any.
 *
 * @param field - The field to read.
 * @param values - The query values in force.
 * @returns The operator, or undefined when the field filters nothing.
 */
export function activeOperator(
  field: ColumnFilterField,
  values: FilterValues
): ColumnFilterOperator | undefined {
  return field.operators.find(operator => {
    const value = values[operator.param]

    return value !== undefined && value !== ''
  })
}

/**
 * The operator the control should show.
 *
 * The one in force wins, so a reader opening a filter sees what is actually
 * filtering. Otherwise it is whichever they last picked on this field while it
 * was empty, and failing that the first the spec declares — which is the one
 * the spec leads with, not an arbitrary choice.
 *
 * @param field - The field to read.
 * @param values - The query values in force.
 * @param preferred - The operator picked while the field was empty, by name.
 * @returns The operator to show. Never undefined: a field declares at least one.
 */
export function chosenOperator(
  field: ColumnFilterField,
  values: FilterValues,
  preferred?: string
): ColumnFilterOperator {
  const active = activeOperator(field, values)
  if (active) {
    return active
  }

  return (
    field.operators.find(operator => operator.op === preferred) ??
    field.operators[0]
  )
}

/**
 * What the field is filtering by, whichever operator holds it.
 *
 * @param field - The field to read.
 * @param values - The query values in force.
 * @returns The value, or an empty string when the field filters nothing.
 */
export function filterValue(
  field: ColumnFilterField,
  values: FilterValues
): string {
  const active = activeOperator(field, values)

  return active ? (values[active.param] ?? '') : ''
}

/** Whether the field is filtering anything at all. */
export function isFiltered(
  field: ColumnFilterField,
  values: FilterValues
): boolean {
  return activeOperator(field, values) !== undefined
}

/**
 * The values with this field cleared.
 *
 * Every parameter the field owns, not just the active one: a stale value in a
 * second parameter would keep filtering after the reader believed they had
 * stopped. This is also what a screen calls when a filtered column is hidden.
 *
 * @param values - The query values in force.
 * @param field - The field to clear.
 * @returns A new value bag. The one passed in is not modified.
 */
export function withoutField(
  values: FilterValues,
  field: ColumnFilterField
): Record<string, string> {
  const next = { ...values }
  for (const param of paramsOf(field)) {
    delete next[param]
  }

  return next
}

/**
 * The values with every one of these fields cleared.
 *
 * Answers the SAME object when none of them was filtering anything, which is
 * what lets a caller skip the work a change would cost — a router navigation, a
 * page reset, a re-filter of every row. `moveColumn` follows the same
 * convention, and for the same reason.
 *
 * @param values - The query values in force.
 * @param fields - The fields to clear.
 * @returns A new value bag, or the one passed in when nothing was cleared.
 */
export function withoutFields(
  values: FilterValues,
  fields: readonly ColumnFilterField[]
): FilterValues {
  const cleared = fields.some(
    field => activeOperator(field, values) !== undefined
  )

  return cleared
    ? fields.reduce<FilterValues>(
        (carried, field) => withoutField(carried, field),
        values
      )
    : values
}

/**
 * The values with this field filtering by one operator and one value.
 *
 * Writes exactly one of the field's parameters and clears the rest, so moving
 * from `is` to `at least` cannot leave both applying. An empty value clears the
 * field outright rather than sending an empty parameter, because the endpoint
 * reads a present-but-empty filter as a filter.
 *
 * @param values - The query values in force.
 * @param field - The field being filtered.
 * @param operator - The operator to write.
 * @param value - The value to write, already joined if it is a range.
 * @returns A new value bag. The one passed in is not modified.
 */
export function withFilter(
  values: FilterValues,
  field: ColumnFilterField,
  operator: ColumnFilterOperator,
  value: string
): Record<string, string> {
  const next = withoutField(values, field)
  if (value !== '') {
    next[operator.param] = value
  }

  return next
}

/**
 * How one filter in force reads to a person.
 *
 * The operator's own label carries the verb, so a boolean field — whose only
 * operator is `is` — needs the value spelled rather than `true`. Nothing else
 * is rewritten: the reader typed it.
 *
 * @param field - The filtered field.
 * @param operator - The operator in force.
 * @param value - The value in force.
 * @param booleanLabel - Spells a boolean value, for example Active / Inactive.
 * @returns The words to show on the chip.
 */
export function filterLabel(
  field: ColumnFilterField,
  operator: ColumnFilterOperator,
  value: string,
  booleanLabel: (on: boolean) => string
): string {
  if (field.kind === 'boolean') {
    return `${field.label}: ${booleanLabel(value === 'true')}`
  }
  if (isRange(operator)) {
    const { from, to } = splitRange(value)

    return `${field.label} ${operator.label} ${from} and ${to}`
  }
  // `SCHEDULED,IN_PROGRESS` is the wire value, not words. The chip is prose, so it gets the
  // separator a reader would write.
  if (isList(operator)) {
    return `${field.label} ${operator.label} ${splitList(value).join(', ')}`
  }

  return `${field.label} ${operator.label} ${value}`
}

/**
 * The URL grammar's filter parameters for a set of fields.
 *
 * A `TableQueryGrammar` names every parameter and the type the URL carries it
 * as, and a field list already names every parameter. Deriving the one from
 * the other is what stops a screen restating its own filter names in a second
 * place, where a renamed operator would leave a value the URL drops in
 * silence.
 *
 * The type follows the control's kind, with one exception on each side of it:
 * a range and a list are text whichever column they sit on, because `10..20`
 * and `1,2,3` are not numbers and `Number` would make `NaN` of both.
 *
 * @param fields - The fields the screen offers.
 * @returns Parameter name → the type the URL writes it as.
 */
export function filterParamsOf(
  fields: readonly ColumnFilterField[]
): Record<string, TableQueryValueType> {
  const params: Record<string, TableQueryValueType> = {}
  for (const field of fields) {
    for (const operator of field.operators) {
      params[operator.param] = paramType(field, operator)
    }
  }

  return params
}

/**
 * What the URL writes one field's operator as.
 *
 * @param field - The field being filtered.
 * @param operator - The operator to type.
 * @returns The value type.
 */
function paramType(
  field: ColumnFilterField,
  operator: ColumnFilterOperator
): TableQueryValueType {
  if (field.kind === 'boolean') {
    return 'boolean'
  }
  if (field.kind === 'number' && !isRange(operator) && !isList(operator)) {
    return 'number'
  }

  // An `enum` reaches here too. Its values are strings on the wire whichever operator carries
  // them, and typing one `number` because its values happen to look numeric would make `NaN` of a
  // code like `01`.
  return 'string'
}
